// Decides whether an app can honestly be rendered inside the portal.
//
// Embedding is not a preference, it is a consequence: an iframe shows the app
// rather than the app's login screen only when the browser sends the portal's
// session cookie to the app's host. That is true exactly when both hosts sit
// under the domain the cookie is scoped to — so this asks that question and
// nothing else, rather than carrying a separate "embedding enabled" flag that
// could disagree with the cookie.
//
// The practical effect: on *.run.app today the server reports no cookie domain,
// every app fails the test, and the pane stays the launch pane it is now. When
// the portal and the tools move under a shared parent and SESSION_COOKIE_DOMAIN
// is set, embedding turns on by itself. There is no flag to arm and no way for
// the two to drift apart.

// Normalises '.tools.bstock.io' / 'tools.bstock.io' to a leading-dot suffix.
// The leading dot is load-bearing: matching the bare suffix 'tools.bstock.io'
// would also accept 'eviltools.bstock.io', which is a different registrable
// host that must not be framed or trusted with the session.
function dotSuffix(cookieDomain) {
    if (!cookieDomain || typeof cookieDomain !== 'string') return null
    const trimmed = cookieDomain.trim().toLowerCase()
    if (!trimmed || trimmed === '.') return null
    const bare = trimmed.startsWith('.') ? trimmed.slice(1) : trimmed
    // Same three-label floor the server's assertSafeCookieDomain enforces, on
    // purpose rather than by trust. Without it hostIsUnder('anything.io', '.io')
    // is true and every .io host reads as embeddable; the only thing preventing
    // that config from existing is a boot-time throw in the auth package, which
    // is a different repo. A client-side gate that depends on a server-side
    // guard it cannot see is one deploy away from being wrong.
    if (bare.split('.').filter(Boolean).length < 3) return null
    return `.${bare}`
}

// True when `host` is the cookie domain itself or a subdomain of it.
export function hostIsUnder(host, cookieDomain) {
    const suffix = dotSuffix(cookieDomain)
    if (!suffix || !host) return false
    const h = String(host).trim().toLowerCase()
    if (!h) return false
    return h === suffix.slice(1) || h.endsWith(suffix)
}

/**
 * @param appUrl       the app's configured URL (from the registry)
 * @param portalHost   window.location.hostname
 * @param cookieDomain the server's SESSION_COOKIE_DOMAIN, or null/'' when unset
 */
export function isEmbeddable({ appUrl, portalHost, cookieDomain }) {
    if (!dotSuffix(cookieDomain)) return false
    if (!hostIsUnder(portalHost, cookieDomain)) return false

    let parsed
    try {
        parsed = new URL(appUrl)
    } catch {
        return false
    }
    // A cookie marked Secure is only sent over https, so an http app would get
    // no session and frame its login screen — the exact dishonesty this guards.
    if (parsed.protocol !== 'https:') return false
    return hostIsUnder(parsed.hostname, cookieDomain)
}
